<template>
    <div class="wrapper">
        <date-time-picker
            ref="picker"
            :lang="context.lang"
            :disabled="disabled"
            :themeColor='provider.theme_tint_hex'
            :noIcon="true"
            v-model="selectedDate"
            :placeholder="placeholder"
            :inputClass="inputClass"
            :timesForDate="timesForDate"
            :isDateDisabled="isDateDisabled"
            :fixedTime="fixedTime"
            :targetMoment="dateTimePickerTargetMoment"
            :showLoadingMask="isLoading"
            :timezoneHint="dateTimePickerTimezoneHint"
            :grayOutHint="grayOutHint"
            @open="initFetchAvailableDateTime"
            @range-change="fetchAvailableDateTime">
        </date-time-picker>
    </div>
</template>

<script>
import dateTimePicker from '../common/DateTimePicker';
import moment from 'moment';
import loadingIndicator from '../common/LoadingIndicator';
import ProviderBookingManager from '../../bu/ProviderBookingManager';
import RootStore from '../../stores/root';
import { mapGetters } from 'vuex';
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import _ from 'lodash';

export default {
    components: { dateTimePicker, loadingIndicator },
    store: RootStore,
    mixins: [api, promptConfirm],
    props: {
        context: { type: Object, required: true },
        provider: { type: Object, required: true },
        service: { type: Object },
        member: { type: Object },
        inputClass: { type: String, required: false },
        disabled: {type: Boolean, default: false},
        extraAvailabilityCheck: { type: Function, required: false },
        fixedTime: { type: String, required: false},
        overwritePlaceholder: { type: String, required: false },
        ignoreServiceStartTime: { type: Boolean, default: false },
        checkCartItems: { type: Boolean, default: false },
        crossOverDisabled: { type: Boolean, default: false },
        startMoment: { type: Object, required: false },
        value: { type: Date }
    },
    watch: {
        service() {
            this.checkSelectedDateValid();
        },
        member() {
            this.checkSelectedDateValid();
        },
        selectedDate() {
            var value = this.selectedDate ? moment(this.selectedDate).toDate() : null;
            this.$emit('input', value);
        },
        requiredGroups() {
            this.checkSelectedDateValid();
        },
        value() {
            this.selectedDate = this.value ? moment(this.value).format() : null;
            this.checkSelectedDateValid();
        },
        startMoment() {
            this.earliestUnavaiableDate = null;
            this.findEarliestUnavaiableDate();
        },
        earliestUnavaiableDate() {
            console.log('earliestUnavaiableDate updatd', [this.earliestUnavaiableDate?.toDate()]);
            this.$forceUpdate();
        },
        availableTimes() {
            this.findEarliestUnavaiableDate();
        },
        currentRange() {
            this.hasLoadedPossibleTimes = false;
        }
    },
    mounted() {
        this.checkSelectedDateValid = _.debounce(function() {
            if (!this.selectedDate || !this.currentRange) {
                return;
            }

            this.fetchAvailableDateTime(this.currentRange, () => {
                if (this.selectedDate) {
                    const possibleTimesForDate = this.possibleTimesForDate(moment(this.selectedDate));
                    if (possibleTimesForDate.length > 0) {
                        if (this.fixedTime) {
                            // 該日有時間且為固定時間，這樣 ok
                        } else {
                            // 該日有時間，但服務不是固定時間，需檢查當下選擇的時間是否正確
                            const selectedTime = moment(this.selectedDate).format('HH:mm');
                            // 可用時間內，不包含當下已選時間，需清除
                            if (possibleTimesForDate.indexOf(selectedTime) === -1) {
                                this.clear();
                            }
                        }
                    } else {
                        this.clear();
                    }
                    this.findEarliestUnavaiableDate();
                }
            });
        }, 500);

        if (this.selectedDate) {
            this.checkSelectedDateValid();
        }
    },
    data() {
        this.bookingManager = new ProviderBookingManager(
            this.provider,
            this.provider.services,
            this.context.userServices,
            this.context.members,
            this.provider.working_times
        );
        return {
            grayOutHint: this.$t('message.gray_out_means_not_available'),
            isLoading: false,
            availableDates: [],
            availableTimes: [],
            selectedDate: this.value ? moment(this.value).format() : null,
            cartItemsUnavailableDates: [],
            earliestUnavaiableDate: null,
            hasLoadedPossibleTimes: false
        };
    },
    computed: {
        ...mapGetters({
            allCartItems: 'cart/items',
            preventCartEmpty: 'cart/preventEmpty',
            items: 'items/items',
            itemMap: 'items/itemMap',
            specMap: 'items/specMap',
            requiredGroups: 'item-groups/requiredGroups',
        }),
        cartItems() {
            return this.allCartItems.filter((item) => item.count > 0 && (item.itemId || item.specId));
        },
        dateTimePickerTimezoneHint() {
            if (moment.tz.guess().toLowerCase() != this.provider.timezone.toLowerCase()) {
                return this.$t('timezone_hint', {timezone: this.provider.timezone});
            } else {
                return null;
            }
        },
        placeholder() {
            if (this.overwritePlaceholder) {
                return this.overwritePlaceholder;
            }

            return this.provider.landing_choose_time_label ? this.$t('message.select_custom', { custom: this.provider.landing_choose_time_label}) : this.$t('message.please_select_datetime_to_book')
        },
        dateTimePickerTargetMoment() {
            if (this.startMoment) {
                return this.startMoment;
            }

            if (!this.service || !this.service.limited_periods ||  this.service.limited_periods.length === 0) {
                return null;
            }

            var now = moment();
            var validPeriods = this.service.limited_periods.filter((p) => {
                return moment.tz(p.end, 'GMT').isAfter(now);
            });
            if (validPeriods.length > 0) {
                var startMoment = moment.tz(validPeriods[0].start, 'GMT');
                if (now.isAfter(startMoment)) {
                    return now;
                }
                return startMoment;
            }
            return null;
        },
        currentRange() {
            const pickerEl = this.$refs.picker;
            if (pickerEl?.opened) {
                return pickerEl.currentRange();
            }

            let targetMoment = this.selectedDate ? moment(this.selectedDate) : this.dateTimePickerTargetMoment;
            if (pickerEl && targetMoment) {
                pickerEl.datesToDisplay = pickerEl.dates(targetMoment.toDate().getFullYear(), targetMoment.toDate().getMonth());
            }

            if (targetMoment) {
                return {
                    start: targetMoment.clone().startOf('month'),
                    end: targetMoment.clone().endOf('month')
                }
            }
        }
    },
    methods: {
        open() {
            this.$refs['picker'].open();
        },
        clear() {
            this.selectedDate = null;
        },
        findEarliestUnavaiableDate() {
            if (!this.hasLoadedPossibleTimes) {
                return;
            }

            this.earliestUnavaiableDate = null;
            // When no current range set, reset to null
            if (!this.currentRange) {
                return;
            }
            // When no exlicit start moment, skip this task
            if (!this.startMoment) {
                return;
            }

            let checkee = moment(this.startMoment);
            if (this.service?.minimum_custom_duration_value) {
                if (this.service.minimum_custom_duration_unit === 'night') {
                    checkee = checkee.startOf('day').add(this.service.minimum_custom_duration_value, 'day');
                } else if (this.service.minimum_custom_duration_unit === 'day') {
                    checkee = checkee.startOf('day').add(this.service.minimum_custom_duration_value - 1, 'day');
                } else {
                    checkee = checkee.add(this.service.minimum_custom_duration_value, this.service.minimum_custom_duration_unit);
                }
            } else {
                checkee = checkee.add(1, 'days');
            }
            while (checkee.isBefore(this.currentRange.end)) {
                if (!checkee.isBefore(this.currentRange.start)) {
                    const disabled = this.timesForDate(checkee.toDate()).length <= 0;
                    if (disabled) {
                        if (!this.earliestUnavaiableDate || this.earliestUnavaiableDate.isAfter(checkee)) {
                            this.earliestUnavaiableDate = checkee;
                            return;
                        }
                    }
                    if (this.earliestUnavaiableDate && checkee.isAfter(this.earliestUnavaiableDate)) {
                        return;
                    }
                }
                checkee.add(1, 'days');
            }
        },
        isDateDisabled(date) {
            if (this.crossOverDisabled && this.startMoment && this.earliestUnavaiableDate) {
                if (date.moment.isSameOrAfter(this.earliestUnavaiableDate)) {
                    return true;
                }
            }
            return this.timesForDate(date.moment.toDate()).length <= 0;
        },
        timesForDate(date) { // date is Date object, not Moment here
            return this.possibleTimesForDate(date);
        },
        possibleTimesForDate(date) {
            const dateString = moment(date).format('YYYY-MM-DD');
            const isoWeekday = moment(date).isoWeekday();
            let availableTimes = this.availableTimes[dateString] || [];

            // 先考慮 必填且內部商品皆有時間限制的 的購物群組 內的商品
            var allRequiredItemIds = {};
            this.requiredGroups.forEach((group) => {
                const groupAvailableItems = group.items.filter((item) => item.is_visible);
                const allItemsAreDateTimeConstrained = groupAvailableItems.every(item => {
                    const hasWeekdayLimit = item.availableWeekdays.length > 0;
                    const hasPeriodLimit = item.availablePeriods.length > 0;
                    const hasSpecDateLimit = item.specs.filter(spec => spec.fixed_date_time).length > 0;
                    return hasWeekdayLimit || hasPeriodLimit || hasSpecDateLimit;
                });
                if (allItemsAreDateTimeConstrained) {
                    groupAvailableItems.forEach((item) => {
                        allRequiredItemIds[item.id] = true
                    });
                }
            });
            var allRequiredItems = Object.keys(allRequiredItemIds).map((id) => this.itemMap[id]);

            // 若沒有必填的購物群組，但有打勾至少購買一個商品，就要看全部商品
            if (allRequiredItems.length === 0 && this.preventCartEmpty) {
                const visibleItems = this.items.filter((item) => item.is_visible);
                const allItemsAreDateTimeConstrained = visibleItems.every(item => {
                    const hasWeekdayLimit = item.availableWeekdays.length > 0;
                    const hasPeriodLimit = item.availablePeriods.length > 0;
                    const hasSpecDateLimit = item.specs.filter(spec => spec.fixed_date_time).length > 0;
                    return hasWeekdayLimit || hasPeriodLimit || hasSpecDateLimit;
                });
                if (allItemsAreDateTimeConstrained) {
                    allRequiredItems = visibleItems;
                }
            }

            if (allRequiredItems.length > 0) {
                availableTimes = availableTimes.filter((time) => {
                    const components = time.split(':');
                    const candidateString = moment(date).hour(components[0]).minute(components[1]).format('YYYY-MM-DD HH:mm:00');
                    const slotMoment = moment(date).hour(components[0]).minute(components[1]);

                    // 此時間需對所有當下商品 ProviderItem 至少有一個滿足
                    return allRequiredItems.some((item) => {
                        // console.log(`檢查商品 ${item.name}`);

                        // 符合 期間限定 
                        if (item.availablePeriods.length > 0) {
                            const hasMatchedAtLeastOnePeriod = item.availablePeriods.some(period => {
                                const start = moment.tz(period.start_datetime, 'GMT').tz(this.provider.timezone);
                                const end = moment.tz(period.end_datetime, 'GMT').tz(this.provider.timezone);
                                const range = moment.range(start, end);
                                const matchedPeriod = range.contains(slotMoment);
                                return matchedPeriod;
                            });
                            if (!hasMatchedAtLeastOnePeriod) {
                                return false;
                            }
                        }
                        // console.log(`格子 ${date} ${time} 滿足任何期間限定`);

                        // 且 符合 週幾限定
                        if (item.availableWeekdays.length > 0) {
                            const hasMatchedAtLeastOneWeekday = item.availableWeekdays.find((weekday) => weekday == isoWeekday);
                            if (!hasMatchedAtLeastOneWeekday) {
                                return false;
                            }
                        }
                        // console.log(`格子 ${date} ${time} 滿足任何週幾限定`);


                        // 且 若規格都有 fixed_date_time 的話，需在符合至少一個 spec fixed_date_time
                        if (item.specs.length > 0) {
                            const hasMatchedAtLeastOneFixedDateTimeSpec = item.specs.some((spec) => {
                                if (!spec.fixed_date_time) {
                                    return true;
                                }
                                const fixedDateTimeString = moment.tz(spec.fixed_date_time.replaceAll('-', '/'), 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD HH:mm:00');
                                return fixedDateTimeString == candidateString;
                            });
                            if (!hasMatchedAtLeastOneFixedDateTimeSpec) {
                                return false;
                            }
                            // console.log(`格子 ${date} ${time} 滿足至少一個規格的固定提供時間`);
                        }
                        

                        return true;
                    });
                });
            }

            if (this.checkCartItems) {
                // 1. 已知購物袋內商品，當天無法提供
                if (this.cartItemsUnavailableDates && this.cartItemsUnavailableDates.length > 0 && this.cartItemsUnavailableDates.indexOf(dateString) >= 0) {
                    return [];
                }

                const items = this.cartItems.map((cartItem) => this.itemMap[cartItem.itemId]);

                // 2. 購物袋內商品，限制週幾的
                const itemsWithWeekLimitation = items.filter(item => item.availableWeekdays.length);
                if (itemsWithWeekLimitation.length) {
                    // 2.1 有週幾限制的商品，是否有符合當下 date isoweekday 的
                    // 如果沒有，就是無法買，回傳空陣列 times
                    const hasSomeItemCanBeBoughtByTheWeekday = itemsWithWeekLimitation.some(item => {
                        return item.availableWeekdays.find(weekday => weekday == isoWeekday);
                    })
                    if (false == hasSomeItemCanBeBoughtByTheWeekday) {
                        return [];
                    }
                }

                // 3. 購物袋內商品，針對期間限定商品，先取商品內所有期間限定區間的聯集，再取所有商品之間期間限定區間的交集
                // 可選的時間，必須符合這個交集
                const itemWithPeriodsLimitation = items.filter(item => item.availablePeriods.length);
                if (itemWithPeriodsLimitation.length) {
                    availableTimes = availableTimes.filter((time) => {
                        const components = time.split(':');
                        return itemWithPeriodsLimitation.every(item => {
                            return item.availablePeriods.some(period => {
                                const start = period.start_datetime;
                                const end = period.end_datetime;
                                const range = moment.range(start, end);
                                const matchedPeriod = range.contains(moment(date).hour(components[0]).minute(components[1]));
                                return matchedPeriod;
                            })
                        })
                    });
                }

                // 4. 購物袋內，有 fixedDateTime 的 spec
                // 可選的時間，必須符合這個 fixedDateTime
                const specFixedDateTimes = this.cartItems
                    .filter((cartItem) => cartItem.specId)
                    .map((cartItem) => this.specMap[cartItem.itemId].find(spec => spec.id == cartItem.specId))
                    .filter(spec => spec.fixed_date_time)
                    .map(spec => spec.fixed_date_time);

                if (specFixedDateTimes.length) {
                    availableTimes = availableTimes.filter(time => {
                        const components = time.split(':');
                        const candidateString = moment(date).hour(components[0]).minute(components[1]).format('YYYY-MM-DD HH:mm:00');
                        return specFixedDateTimes.filter((fixedDateTime) => {
                            const fixedDateTimeString = moment.tz(fixedDateTime, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD HH:mm:00');
                            return candidateString == fixedDateTimeString
                        }).length === specFixedDateTimes.length;
                    })
                }
            }
            return availableTimes;
        },
        initFetchAvailableDateTime() {
            this.fetchAvailableDateTime(this.$refs.picker.currentRange());
        },
        fetchAvailableDateTime(range, cb) {
            const pickerEl = this.$refs.picker;
            // 有顯示出來的 picker 才會執行檢查，避免多個 picker 重複打一樣的 api 與檢查邏輯
            if (pickerEl && pickerEl.$el && (pickerEl.$el.offsetWidth == 0 || pickerEl.$el.offsetHeight == 0)) {
                return;
            }
            if (!this.service || !this.member) {
                return;
            }

            this.isLoading = true;
            this.bookingManager.getAvailableDateTimeForServiceAndMember({
                provider: this.provider,
                service: this.service,
                member: this.member,
                startMoment: this.startMoment,
                startDate: range.start,
                endDate: range.end,
                skipBookingId: null,
                ignoreServiceStartTime: this.ignoreServiceStartTime,
                callback: (response, error) => {
                    var dates = response.dates;
                    var times = response.times;
                    if (this.extraAvailabilityCheck) {
                        for(var date in times) {
                            times[date] = times[date].filter((time) => {
                                return this.extraAvailabilityCheck(moment(`${date} ${time}`));
                            });
                        }
                    }

                    if (this.checkCartItems) {
                        const itemIds = this.cartItems.filter((item) => !item.specId).map((item) => item.itemId);
                        const specIds = this.cartItems.filter((item) => item.specId).map((item) => item.specId);
                        this.getCartItemsUnavailableDateByStock(range.start.format('YYYY-MM-DD'), range.end.format('YYYY-MM-DD'), itemIds, specIds, (response, error) => {
                            this.cartItemsUnavailableDates = response.data;
                            if (this.cartItemsUnavailableDates.length === (this.$refs.picker?.datesToDisplay || []).flatMap((item) => item).length) {
                                this.grayOutHint = this.$t('message.cart_items_all_not_available_in_this_month');
                            } else {
                                this.grayOutHint = this.$t('message.gray_out_means_not_available');
                            };
                        });
                    }
                    this.availableDates = dates;
                    this.availableTimes = times;
                    if (cb) {
                        cb();
                    }
                    this.isLoading = false;
                    this.hasLoadedPossibleTimes = true;
                    this.$refs.picker.$forceUpdate();
                }
            });
        }
    }
}
</script>

<style lang="scss" scoped>
    .wrapper {
        cursor: pointer;
    }

</style>

<i18n>
    {
        "en": {
            "timezone_hint": "Timezone {timezone}"
        },
        "zh": {
            "timezone_hint": "時區 {timezone}"
        }
    }
</i18n>